import { Box, Fade, Flex, Grid, GridItem, HStack, IconButton, Table, Tbody, Td, Text, Th, Thead, Tr, useToast } from "@chakra-ui/react";
import { ArrowLeft, CaretDown, CaretUp, ChartDonut, ListBullets } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import LocalSpinner from "../../../../components/LocalSpinner";
import { Page } from "../../../../components/Page";
import { H2, H3 } from "../../../../components/text/headings";
import { routes } from "../../../../config";
import { useGetSuppliersYearlyData } from "../../../../hooks/query";
import { useGetSupplierOverviewData } from "../../../../hooks/query/emissions-insight/emissions-insights-query";
import { constructQueryParamsString, getFormattedNumValue, showErrorToast } from "../../../../utils";
import YoYChip from "../components/YoYChip";
import { getYoYChange } from "../../../../utils/yoy";
import SupplierKpiCard from "./SupplierKpiCard";
import TargetBar from "./TargetBar";
import SupplierComparison from "./SupplierComparison";
import SupplierFiltersCatQualityYear from "./SupplierFiltersCatQualityYear";
import A from "components/A";
import * as React from "react";
import { ToggleButton } from "./SupplierCards";
import { ResponsiveBump } from "@nivo/bump";
import { TrendCalculationMethodDropdown } from "./SupplierTrends";
import { shortenText } from "utils/text-utils";

const SUPPLIER_TABLE_ACCENT = "#5B1D99";

const SupplierInsightEmissionDetail = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [currentYearData, setCurrentYearData] = useState(null);
  const [previousYearData, setPreviousYearData] = useState(null);
  const [yearByYearEmissionsData, setYearByYearEmissionsData] = useState([]);
  const [filters, setFilters] = useState();
  const [selectedViewType, setSelectedViewType] = useState("table");

  const { data: overviewData, isFetching: isLoading } = useGetSupplierOverviewData(
    [filters?.year, filters?.year - 1],
    filters?.categories,
    filters?.dataQuality,
    null,
    {
      onError: (error) => {
        console.error("Error fetching supplier overview data:", error);
        showErrorToast(toast, "Failed to fetch supplier overview data");
      },
      cacheTime: 60, // 1 minute
    }
  );

  useEffect(() => {
    if (overviewData) {
      setCurrentYearData(overviewData[filters?.year]);
      setPreviousYearData(overviewData[filters?.year - 1]);
    }
  }, [overviewData, filters?.year]);

  const getQueryParamsString = () => {
    let year = filters?.year;
    if (!year) {
      // HACK default - which will also get set when filters is initialised
      year = new Date().getFullYear();
    }
    const currentYear = new Date().getFullYear();
    const yearsSet = new Set([currentYear, year]);
    let yearToAdd = currentYear - 1;
    while (yearsSet.size < 5 && yearToAdd >= 2021) {
      // assuming data is available from 2021 onwards
      yearsSet.add(yearToAdd);
      yearToAdd--;
    }
    const years = Array.from(yearsSet).sort((a, b) => b - a);

    const queryParams = {
      years: years,
      categories: filters?.categories,
      dataQuality: filters?.dataQuality,
      filterYear: year,
    };
    return constructQueryParamsString(queryParams);
  };

  const { isFetching: isSuppliersFetching } = useGetSuppliersYearlyData(getQueryParamsString(), {
    onSuccess: (data) => {
      // exclude data where excludeFromReport is true
      const filteredData = data
        .filter((item) => !item.excludeFromReport)
        .map((item) => {
          const current = item.yearlyEmissionData?.[filters?.year]?.totalCo2Emissions ?? 0;
          const prev = item.yearlyEmissionData?.[filters?.year - 1]?.totalCo2Emissions ?? 0;
          const growth = prev !== 0 ? ((current - prev) / Math.abs(prev)) * 100 : null;
          return { ...item, growth };
        });
      setYearByYearEmissionsData(filteredData);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  return (
    <Page>
      <Flex direction="column" gap={2} mx="10%">
        <Flex onClick={() => navigate(routes.reportingPortalSupplierInsights)} align="center" gap={2} cursor="pointer">
          <ArrowLeft size={22} color="#85C22A" />
          <Text>Value Chain</Text>
        </Flex>

        <H2 fontSize="heading.xl">Supplier Attributable Emissions</H2>

        <Text maxW="75%" color="gray.500" fontSize="body.m">
          Review and analyse the data provided by suppliers
        </Text>

        <Flex direction="column" gap={4}>
          <SupplierFiltersCatQualityYear
            filters={filters}
            setFilters={setFilters}
            showFilters={["year", "quality", "category"]}
          />
        </Flex>

        <Flex direction="column" gap={8} mt={6}>
          <Cards currentYearData={currentYearData} previousYearData={previousYearData} isLoading={isLoading} />
          <Box>
            <Text fontSize="22px" fontWeight="700" color="darkTeal">
              Attributable Emissions
            </Text>
            <Text fontSize="13px" color="gray.500">
              Per-supplier attributed emissions by scope for {filters?.year}, with growth and share of the total.
            </Text>
          </Box>

          {/* View Type toggle */}
          <Flex width="fit-content" alignSelf="flex-end" mt={2}>
            <ToggleButton
              options={[
                { label: "Table", value: "table", icon: <ListBullets size={18} /> },
                { label: "Visual", value: "visual", icon: <ChartDonut size={18} /> },
              ]}
              selected={selectedViewType}
              setSelected={setSelectedViewType}
            />
          </Flex>

          <Fade in={selectedViewType === "table"} unmountOnExit>
            <SuppliersTable
              year={filters?.year}
              yearByYearEmissionsData={yearByYearEmissionsData}
              isLoading={isSuppliersFetching}
            />
          </Fade>

          <Fade in={selectedViewType === "visual"} unmountOnExit>
            <SuppliersBumpChart yearByYearEmissionsData={yearByYearEmissionsData} isLoading={isSuppliersFetching} />
          </Fade>

          <SupplierComparison
            year={filters?.year}
            yearByYearEmissionsData={yearByYearEmissionsData}
            isLoading={isSuppliersFetching}
          />
        </Flex>
      </Flex>
    </Page>
  );
};

const Cards = ({ currentYearData, previousYearData, isLoading }) => {
  // Tracking: how far actuals are over/under target, as a signed %. Positive =
  // over target (bad, red); negative = under target (good, green). The headline
  // IS this percentage; the actual vs target figures sit in the footer.
  const actual = currentYearData?.totalEmissions;
  const target = currentYearData?.totalTarget;
  const trackingDelta =
    typeof actual === "number" && typeof target === "number" && target !== 0
      ? ((actual - target) / target) * 100
      : null;
  const overTarget = trackingDelta != null && trackingDelta > 0;
  const trackingValue =
    trackingDelta == null
      ? "—"
      : `${overTarget ? "+" : ""}${trackingDelta.toLocaleString("en-GB", { maximumFractionDigits: 1 })} %`;

  return (
    <Grid templateColumns="repeat(3, 1fr)" gap={6} mb={6}>
      <GridItem>
        <SupplierKpiCard
          accent="pureBlack"
          title="Total"
          subtitle="All attributed emissions"
          value={formatTco2(currentYearData?.totalEmissions)}
          pctChange={getYoYChange(currentYearData?.totalEmissions, previousYearData?.totalEmissions)}
          loading={isLoading}
        />
      </GridItem>
      <GridItem>
        <SupplierKpiCard
          accent="#5EB5F3"
          title="Intensity"
          subtitle="Emissions per £1m spend"
          value={formatTco2(currentYearData?.spendIntensity)}
          unit="tCO₂e / £m"
          pctChange={getYoYChange(currentYearData?.spendIntensity, previousYearData?.spendIntensity)}
          loading={isLoading}
        />
      </GridItem>
      <GridItem>
        <SupplierKpiCard
          accent="lime"
          title="Tracking"
          subtitle={trackingDelta == null ? "vs target" : overTarget ? "over target" : "under target"}
          value={trackingValue}
          valueColor={trackingDelta == null ? "darkTeal" : overTarget ? "red.600" : "green.600"}
          unit={null}
          footerValue={formatTco2(actual)}
          footerLabel={`tCO₂e vs ${formatTco2(target) ?? "—"} target`}
          loading={isLoading}
        >
          <TargetBar delta={trackingDelta} />
        </SupplierKpiCard>
      </GridItem>
    </Grid>
  );
};

// Two-decimal tCO₂e number formatter (matches the carbon detail cards).
const formatTco2 = (v) =>
  typeof v === "number" ? v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : null;

/**
 * data comes from getSupplierYearlyData()
 * @param {*} param0
 * @returns
 */
// Hand-built table matching the carbon by-dimension tables (grey header,
// accent square, share-of-total bar, inline YoYChip growth, bold total row).
// Local to this page by design (see frontend/CLAUDE.md — do not use a shared
// DataTableV2). Client-side sortable; defaults to Total descending.
const SuppliersTable = ({ year, yearByYearEmissionsData, isLoading }) => {
  const [sort, setSort] = useState({ key: "total", dir: "desc" });

  const rows = useMemo(() => {
    const mapped = (yearByYearEmissionsData || []).map((s) => {
      const y = s.yearlyEmissionData?.[year] || {};
      const scope1 = Number(y.scope1) || 0;
      const scope2 = Number(y.scope2) || 0;
      const scope3 = Number(y.scope3) || 0;
      const total = Number(y.totalCo2Emissions) || 0;
      return {
        supplierId: s.supplierId,
        supplierName: s.supplierName,
        scope1,
        scope2,
        scope3,
        total,
        intensity: Number(y.intensity) || 0,
        growth: typeof s.growth === "number" ? s.growth : null,
        // Supplier submitted only a product/location questionnaire — scope
        // breakdown is not applicable (those emissions don't map to scopes).
        productOnly: total > 0 && scope1 === 0 && scope2 === 0 && scope3 === 0,
      };
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    return mapped.sort((a, b) => {
      if (sort.key === "supplierName") return dir * String(a.supplierName || "").localeCompare(String(b.supplierName || ""));
      const av = a[sort.key] ?? 0;
      const bv = b[sort.key] ?? 0;
      return dir * ((av || 0) - (bv || 0));
    });
  }, [yearByYearEmissionsData, year, sort]);

  const grandTotal = rows.reduce((acc, r) => acc + (r.total || 0), 0);
  const totals = rows.reduce(
    (acc, r) => ({
      scope1: acc.scope1 + r.scope1,
      scope2: acc.scope2 + r.scope2,
      scope3: acc.scope3 + r.scope3,
      total: acc.total + r.total,
    }),
    { scope1: 0, scope2: 0, scope3: 0, total: 0 }
  );

  const toggleSort = (key) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));

  // Numeric column header with a sort affordance.
  const NumHead = ({ label, sortKey }) => (
    <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">
      <HStack spacing={1} justify="flex-end" cursor="pointer" onClick={() => toggleSort(sortKey)} _hover={{ color: "gray.700" }}>
        <Text as="span">{label}</Text>
        {sort.key === sortKey && (sort.dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
      </HStack>
    </Th>
  );

  if (isLoading) return <LocalSpinner />;

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
      <Table size="sm" variant="unstyled">
        <Thead>
          <Tr bg="gray.50">
            <Th py={3} px={4} fontSize="11px" color={SUPPLIER_TABLE_ACCENT} textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" textAlign="left">
              <HStack spacing={2} cursor="pointer" onClick={() => toggleSort("supplierName")}>
                <Box w="8px" h="8px" bg={SUPPLIER_TABLE_ACCENT} borderRadius="sm" />
                <Text as="span">Supplier</Text>
                {sort.key === "supplierName" && (sort.dir === "asc" ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
              </HStack>
            </Th>
            <NumHead label="Scope 1" sortKey="scope1" />
            <NumHead label="Scope 2" sortKey="scope2" />
            <NumHead label="Scope 3" sortKey="scope3" />
            <NumHead label="Total (tCO₂e)" sortKey="total" />
            <NumHead label="Intensity" sortKey="intensity" />
            <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">
              Growth vs prev
            </Th>
            <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">
              Share of total
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.length === 0 ? (
            <Tr>
              <Td colSpan={8} py={6} textAlign="center" color="gray.400" fontSize="13px">
                No supplier emissions data for this year.
              </Td>
            </Tr>
          ) : (
            rows.map((r, idx) => {
              const share = grandTotal > 0 ? r.total / grandTotal : 0;
              return (
                <Tr key={r.supplierId ?? r.supplierName} borderTop={idx === 0 ? "none" : "1px solid"} borderColor="gray.100">
                  <Td py={3} px={4} fontSize="body.xs" textAlign="left">
                    <HStack spacing={2}>
                      <Box w="8px" h="8px" bg={SUPPLIER_TABLE_ACCENT} borderRadius="sm" flexShrink={0} />
                      <A
                        color="lime.500"
                        textDecoration="underline"
                        href={`${routes.supplierInsightsIndividualDetail}?year=${year}&supplierId=${r.supplierId}`}
                      >
                        {r.supplierName}
                      </A>
                    </HStack>
                  </Td>
                  <Td py={3} px={4} fontSize="body.xs" color={r.productOnly ? "gray.300" : "gray.700"} textAlign="right">{r.productOnly ? "—" : getFormattedNumValue(r.scope1, 2)}</Td>
                  <Td py={3} px={4} fontSize="body.xs" color={r.productOnly ? "gray.300" : "gray.700"} textAlign="right">{r.productOnly ? "—" : getFormattedNumValue(r.scope2, 2)}</Td>
                  <Td py={3} px={4} fontSize="body.xs" color={r.productOnly ? "gray.300" : "gray.700"} textAlign="right">{r.productOnly ? "—" : getFormattedNumValue(r.scope3, 2)}</Td>
                  <Td py={3} px={4} fontSize="body.xs" color="gray.800" fontWeight="600" textAlign="right">{getFormattedNumValue(r.total, 2)}</Td>
                  <Td py={3} px={4} fontSize="body.xs" color="gray.700" textAlign="right">{getFormattedNumValue(r.intensity, 2)}</Td>
                  <Td py={3} px={4} textAlign="left">
                    {/* Growth in attributed emissions — an increase is worse, matching YoYChip's up=red. */}
                    <YoYChip pctChange={r.growth} />
                  </Td>
                  <Td py={3} px={4} textAlign="left" minW="200px">
                    <HStack spacing={3}>
                      <Box flex="1" h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                        <Box h="100%" w={`${share * 100}%`} bg={SUPPLIER_TABLE_ACCENT} borderRadius="full" />
                      </Box>
                      <Text fontSize="12px" color="gray.700" fontWeight="600" minW="44px" textAlign="right">
                        {(share * 100).toFixed(1)}%
                      </Text>
                    </HStack>
                  </Td>
                </Tr>
              );
            })
          )}
          {rows.length > 0 && (
            <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
              <Td py={4} px={4} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" textAlign="left">Total</Td>
              <Td py={4} px={4} fontSize="body.xs" color="darkTeal" fontWeight="700" textAlign="right">{getFormattedNumValue(totals.scope1, 2)}</Td>
              <Td py={4} px={4} fontSize="body.xs" color="darkTeal" fontWeight="700" textAlign="right">{getFormattedNumValue(totals.scope2, 2)}</Td>
              <Td py={4} px={4} fontSize="body.xs" color="darkTeal" fontWeight="700" textAlign="right">{getFormattedNumValue(totals.scope3, 2)}</Td>
              <Td py={4} px={4} fontSize="body.xs" color="darkTeal" fontWeight="700" textAlign="right">{getFormattedNumValue(totals.total, 2)}</Td>
              <Td py={4} px={4} />
              <Td py={4} px={4} />
              <Td py={4} px={4} textAlign="right">
                <Text fontSize="12px" color="darkTeal" fontWeight="700">100.0%</Text>
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
};

const SuppliersBumpChart = ({ yearByYearEmissionsData, isLoading }) => {
  const [method, setMethod] = useState("Emission");

  if (isLoading) {
    return <LocalSpinner />;
  }

  function convertEmissionDataV1(inputData) {
    return inputData?.map((supplier) => {
      // Extract yearly emission data
      const yearlyData = supplier?.yearlyEmissionData;

      // Convert each year's data to the required format
      const dataPoints = Object.entries(yearlyData)?.map(([year, emissions]) => ({
        x: year,
        y: emissions?.totalCo2Emissions !== 0 ? Number(emissions?.totalCo2Emissions) : null,
      }));

      return {
        id: supplier?.supplierName,
        data: dataPoints,
      };
    });
  }

  /**
   * Converts supplier emission data to format required by Nivo ResponsiveBump chart.
   * Inverts emission values so higher emissions appear higher on the chart (lower rank).
   *
   * @param {Array} inputData - Array of supplier objects with yearlyEmissionData
   * @returns {Array} Array of series objects for Nivo bump chart
   */
  function convertEmissionData(inputData, method = "Emission") {
    if (!inputData || inputData.length === 0) return [];

    // Collect all unique years across all suppliers
    const years = new Set();
    inputData.forEach((supplier) => {
      Object.keys(supplier?.yearlyEmissionData || {}).forEach((year) => years.add(year));
    });

    const yearList = Array.from(years).sort();

    // Determine which data field to use based on method
    const dataField = method === "Intensity" ? "intensity" : "totalCo2Emissions";

    // Calculate global maximum value once for inversion
    const maxValue = Math.max(
      ...inputData.flatMap((supplier) =>
        Object.values(supplier?.yearlyEmissionData || {}).map((data) => data?.[dataField] || 0)
      )
    );

    return inputData.map((supplier) => ({
      id: supplier?.supplierName,
      data: yearList.map((year) => {
        const value = supplier?.yearlyEmissionData?.[year]?.[dataField] || 0;

        // Invert value so higher values = higher y position on chart
        const invertedValue = value > 0 ? maxValue - value : null;

        return {
          x: year,
          y: invertedValue,
          value: value, // Store original value for tooltip display
        };
      }),
    }));
  }

  const barHeight = 40;
  const minHeight = 500;
  const chartHeight = Math.max(minHeight, yearByYearEmissionsData?.length * barHeight);

  return (
    <Box>
      <Flex justify="space-between" align="flex-end" mb={3} gap={3} flexWrap="wrap">
        <Box>
          <Text fontSize="body.m" fontWeight="600" color="gray.700">
            Supplier ranking over time
          </Text>
          <Text fontSize="body.xs" color="gray.500">
            How suppliers rank by {method === "Intensity" ? "emissions intensity" : "total emissions"} across years —
            higher on the chart means higher emissions.
          </Text>
        </Box>
        <TrendCalculationMethodDropdown setSelected={setMethod} selected={method} />
      </Flex>
      <Box height={`${chartHeight}px`} width="100%" mt={4}>
        <ResponsiveBump
          data={convertEmissionData(yearByYearEmissionsData, method)}
          margin={{ top: 20, right: 20, bottom: 40, left: 150 }}
          startLabel={(serie) => shortenText(serie.id, 20, 15)}
          startLabelTextColor={{ from: "serie.color" }}
          endLabel={false}
          colors={{ scheme: "spectral" }}
          lineWidth={3}
          activeLineWidth={6}
          inactiveLineWidth={3}
          inactiveOpacity={0.15}
          pointSize={10}
          activePointSize={16}
          inactivePointSize={0}
          pointColor={{ theme: "background" }}
          pointBorderWidth={3}
          activePointBorderWidth={3}
          pointBorderColor={{ from: "serie.color" }}
          // axisRight={{
          //   legend: 'Ranking',
          //   legendOffset: 80,
          //   tickPadding: 5,
          // }}
          axisLeft={null}
          useMesh={true}
          pointTooltip={({ point }) => (
            <Box bg="white" p={3} borderRadius="md" boxShadow="lg" border="1px solid" borderColor="gray.200">
              <Text fontWeight="bold" color="gray.800">
                {point.serie.id}
              </Text>
              <Text color="gray.600" fontSize="sm">
                Year: {point.data.x}
              </Text>
              <Text color="gray.600" fontSize="sm">
                {method === "Intensity" ? "Intensity" : "Emissions"}: {getFormattedNumValue(point.data.value, 2, true)}{" "}
                {method === "Intensity" ? "" : "tCO₂e"}
              </Text>
            </Box>
          )}
        />
      </Box>
      <Text fontSize="body.xs" color="gray.500" mt={2}>
        Lines that climb over time are suppliers whose emissions are rising relative to their peers.
      </Text>
    </Box>
  );
};

export default SupplierInsightEmissionDetail;
